import React from "react";
import { Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import BlockIcon from "@mui/icons-material/Block";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { DATA_GRID_FONT_SIZE } from "../../../components/ui/dataGridStyles";
import { EstadoBadge } from "../components/ui";
import { formatearDireccion } from "./direccion";
import { formatearFechaHora } from "./fechas";
import { RECOGIDA_ESTADO, RECOGIDA_ESTADOS_CANCELABLES } from "../constants";

/**
 * Columnas del DataGrid de recogidas
 */
export const generateRecogidaColumns = ({ onCancelar, onRecepcionar, onEliminar, onVerSeguimiento }) => [
        {
            field: "id",
            headerName: "ID",
            width: 70,
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 600, fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                    #{params.value}
                </Typography>
            ),
        },
        {
            field: "numeroTicket",
            headerName: "Ticket",
            width: 120,
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 600, fontSize: DATA_GRID_FONT_SIZE.CELDA }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: "clienteNombre",
            headerName: "Cliente",
            flex: 1,
            minWidth: 130,
        },
        {
            field: "direccionCompleta",
            headerName: "Dirección",
            flex: 2,
            minWidth: 170,
            valueGetter: (params) => formatearDireccion(params.row.direccion),
        },
        {
            field: "estado",
            headerName: "Estado",
            width: 130,
            renderCell: (params) => <EstadoBadge estado={params.value} />,
        },
        {
            // Lo asigna MSoft al dar de alta; queda a null si el alta fue rechazada
            field: "idRecogidaMsoft",
            headerName: "Código",
            width: 130,
            renderCell: (params) => (
                <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: params.value ? "#495057" : "#6c757d" }}>
                    {params.value || '—'}
                </Typography>
            ),
        },
        {
            field: "situacionMsoft",
            headerName: "Seguimiento",
            width: 150,
            // La situación es texto libre de MSoft y vive dentro de seguimiento, así que
            // hace falta valueGetter para que ordenar y filtrar funcionen sobre ella
            valueGetter: (params) => params.row.seguimiento?.situacionMsoft || '',
            renderCell: (params) => (
                <Typography
                    sx={{
                        fontSize: DATA_GRID_FONT_SIZE.CELDA,
                        color: params.value ? "#495057" : "#6c757d",
                        cursor: "pointer",
                        textDecoration: "underline dotted",
                    }}
                    title="Ver seguimiento"
                    onClick={() => onVerSeguimiento(params.row)}
                >
                    {params.value || 'Sin sincronizar'}
                </Typography>
            ),
        },
        {
            field: "usuario",
            headerName: "Usuario",
            width: 110,
            valueGetter: (params) => params.row.usuario?.username || '',
            renderCell: (params) => (
                <Typography sx={{ fontSize: DATA_GRID_FONT_SIZE.CELDA, color: params.value ? "#495057" : "#6c757d" }}>
                    {params.value || '—'}
                </Typography>
            ),
        },
        {
            // Es la fecha por la que filtran `desde`/`hasta` del listado
            field: "creado",
            headerName: "Creada",
            width: 145,
            type: "dateTime",
            valueGetter: (params) => (params.row.creado ? new Date(params.row.creado) : null),
            valueFormatter: (params) => formatearFechaHora(params.value),
        },
        {
            field: "modificado",
            headerName: "Modificada",
            width: 145,
            type: "dateTime",
            valueGetter: (params) => (params.row.modificado ? new Date(params.row.modificado) : null),
            valueFormatter: (params) => formatearFechaHora(params.value),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Acciones",
            width: 100,
            sortable: false,
            filterable: false,
            getActions: (params) => {
                const estado = params.row.estado;
                const puedeEliminar = estado === RECOGIDA_ESTADO.SOLICITADA || estado === RECOGIDA_ESTADO.ERROR;
                // Solo se cancela lo que aún no ha terminado; recepcionar cierra el ciclo
                // y únicamente tiene sentido sobre una recogida ya realizada
                const puedeCancelar = RECOGIDA_ESTADOS_CANCELABLES.includes(estado);
                const puedeRecepcionar = estado === RECOGIDA_ESTADO.REALIZADA;

                return [
                    <GridActionsCellItem
                        icon={<BlockIcon />}
                        label="Cancelar recogida"
                        onClick={() => onCancelar(params.row)}
                        disabled={!puedeCancelar}
                        showInMenu
                        sx={{ color: '#495057' }}
                    />,
                    <GridActionsCellItem
                        icon={<WarehouseIcon />}
                        label="Recibida en almacén"
                        onClick={() => onRecepcionar(params.row)}
                        disabled={!puedeRecepcionar}
                        showInMenu
                        sx={{ color: '#495057' }}
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Eliminar"
                        onClick={() => onEliminar(params.row)}
                        disabled={!puedeEliminar}
                        showInMenu
                        sx={{ color: '#6c757d' }}
                    />,
                    <GridActionsCellItem
                        icon={<SyncIcon />}
                        label="Seguimiento"
                        onClick={() => onVerSeguimiento(params.row)}
                        showInMenu={false}
                        sx={{ color: '#495057' }}
                    />,
                ];
            },
        },
];
